import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { captureSlowOperation } from "../../sentry";

const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_API_BASE_URL || "http://localhost:8020",
  credentials: "include",
  prepareHeaders: (headers, { endpoint, getState }) => {
    const isFileUpload = endpoint === "uploadImage" || endpoint === "uploadExcel";
    if (!isFileUpload) {
      headers.set("Content-Type", "application/json");
    }

    const token = getState().auth.token;
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }

    return headers;
  },
});

const baseQueryWithReauth = async (args, api, extraOptions) => {
  const start = Date.now();
  let result = await baseQuery(args, api, extraOptions);

  if (result?.error?.status === 403 || result?.error?.status === 401) {
    const refreshResult = await baseQuery(
      { url: "/api/v1/auth/refresh", method: "POST" },
      api,
      extraOptions
    );

    if (refreshResult?.data?.access_token) {
      const { setCredentials } = await import("../../authSlice");
      api.dispatch(
        setCredentials({
          accessToken: refreshResult.data.access_token,
          user: refreshResult.data,
        })
      );
      localStorage.setItem("token", refreshResult.data.access_token);
      result = await baseQuery(args, api, extraOptions);
    } else {
      const { logOut } = await import("../../authSlice");
      api.dispatch(logOut());
    }
  }

  const durationMs = Date.now() - start;
  const thresholdMs = Number(process.env.REACT_APP_SENTRY_SLOW_API_THRESHOLD_MS || "3000");
  if (durationMs >= thresholdMs) {
    const url = typeof args === "string" ? args : args?.url || "unknown";
    const method = typeof args === "string" ? "GET" : args?.method || "GET";
    captureSlowOperation("console_frontend_api_call", durationMs, thresholdMs, {
      api: "console",
      endpoint: api.endpoint,
      url,
      method,
      status: result?.error?.status || "ok",
    });
  }

  return result;
};

export const consoleApiSlice = createApi({
  reducerPath: "consoleApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    "DataCollectionStatus",
    "StagingTemplates",
    "ProdTemplates",
    "SyncStatus",
    "PlaygroundRuns",
    "PlaygroundRun",
    "CustomerNps",
    "CustomerOverview",
    "CustomerEvents",
    "BiActivityTrends",
    "BiUserActivity",
    "BiBrandActiveUsers",
    "BiActivityByWebsite",
    "BiStats",
    "BiFilterOptions",
    "WebsiteAcquisitionStatusV2",
    "ScopeStatus",
    "ScopeUrls",
    "ScopeTrackerWebsites",
    "CostSummary",
    "CostMonthlySummary",
    "CostTrends",
    "CostBreakdown",
    "CostTopScans",
    "CostFilterOptions",
    "CostScanEfficiency",
    "CostStepDistribution",
    "ScanMetadata",
    "ScanTagBreakdown",
    "PipelineStatus",
    "QueueStatus",
    "AgentMemoryEnvironments",
    "AgentMemoryWebsites",
    "AgentMemoryFiles",
    "AgentMemoryFile",
    "AgentMemoryVersions",
    "AgenticRunsEnvironments",
    "AgenticRuns",
    "AgenticRunDetail",
    "AgenticRunEvents",
    "FanOutArtifacts",
    "FanOutScopeDiff",
    "AgentOpsStatus",
    "AgentOpsAgents",
    "AgentOpsBatches",
    "AgentOpsGrouping",
    "AgentOpsGroupingWebsite",
    "AgentOpsGroupingLogs",
    "AgentOpsGroupingHistory",
    "AgentOpsConfigs",
    "AgentSchedules",
    "TeamUsage",
    "BudgetConfig",
    "AlertConfig",
    "CustomAgents",
    "CustomAgentFile",
    "EvalRuns",
    "EvalFilterOptions",
    "EvalRunDetail",
    "EvalTimeline",
    "EvalGuardrails",
  ],
  endpoints: () => ({}),
});
